import { useCallback, useEffect, useMemo, useRef } from 'react'
import constant from 'lodash/constant'
import noop from 'lodash/noop'
import { STEP_ID, STEP_LABELS } from '@klear/payable/constants'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import type { PayableStepCtx } from '@/app/payable/workflow/PayableWizardContext'
import {
  initialStepForMode,
  isPayableHostStepSatisfied,
  type PayableWizardApi,
  type PayableWizardMode
} from '@/app/payable/workflow/usePayableWizard'
import SupplierStep from '@/app/payable/workflow/steps/SupplierStep'
import OperationalStep from '@/app/payable/workflow/steps/OperationalStep'
import PaymentStep from '@/app/payable/workflow/steps/PaymentStep'
import ReviewStep from '@/app/payable/workflow/steps/ReviewStep'

const URL_SYNC_DEBOUNCE_MS = 250

const supplierModule: WizardStepModule<PayableStepCtx> = {
  id: STEP_ID.SUPPLIER,
  label: STEP_LABELS[STEP_ID.SUPPLIER],
  isComplete: (ctx) => isPayableHostStepSatisfied(STEP_ID.SUPPLIER, ctx.getForm()),
  component: SupplierStep
}

const operationalModule: WizardStepModule<PayableStepCtx> = {
  id: STEP_ID.OPERATIONAL,
  label: STEP_LABELS[STEP_ID.OPERATIONAL],
  isComplete: (ctx) => isPayableHostStepSatisfied(STEP_ID.OPERATIONAL, ctx.getForm()),
  component: OperationalStep
}

const paymentModule: WizardStepModule<PayableStepCtx> = {
  id: STEP_ID.PAYMENT,
  label: STEP_LABELS[STEP_ID.PAYMENT],
  isComplete: (ctx) => isPayableHostStepSatisfied(STEP_ID.PAYMENT, ctx.getForm()),
  component: PaymentStep
}

const reviewModule: WizardStepModule<PayableStepCtx> = {
  id: STEP_ID.REVIEW,
  label: STEP_LABELS[STEP_ID.REVIEW],
  isComplete: constant(false),
  component: ReviewStep
}

const PAYABLE_STEP_MODULES: Array<WizardStepModule<PayableStepCtx>> = [
  supplierModule,
  operationalModule,
  paymentModule,
  reviewModule
]

export type PayableFlowProps = {
  workflow: PayableWizardApi
  mode: PayableWizardMode
  payableId?: number
  orderIdSeed?: number | null
  canEdit?: boolean
  onEdit?: () => void
  onNavStateChange: (nav: WizardStepGroupNavState) => void
}

export function PayableFlow({
  workflow,
  mode,
  payableId,
  orderIdSeed,
  canEdit = true,
  onEdit,
  onNavStateChange
}: PayableFlowProps) {
  const readOnly = workflow.readOnly

  const goNextAfterWriteRef = useRef<() => void>(noop)

  const patchFormAndGoNext = useCallback(
    (
      recipe: Parameters<PayableWizardApi['patchForm']>[0],
      dirtyPaths?: Parameters<PayableWizardApi['patchForm']>[1]
    ) => {
      workflow.patchForm(recipe, dirtyPaths)
      goNextAfterWriteRef.current()
    },
    [workflow.patchForm]
  )

  const attachExistingOperational = useCallback(
    (operationalEventId: number) => {
      workflow.attachExistingOperational(operationalEventId)
    },
    [workflow.attachExistingOperational]
  )

  const goNext = useCallback(() => {
    goNextAfterWriteRef.current()
  }, [])

  const stepCtx = useMemo<PayableStepCtx>(
    () => ({
      mode: workflow.mode,
      readOnly: workflow.readOnly,
      form: workflow.form,
      getForm: workflow.getForm,
      dirty: workflow.dirty,
      patchForm: workflow.patchForm,
      patchFormAndGoNext,
      goNext,
      operationalLinkLocked: workflow.operationalLinkLocked,
      removeOperational: workflow.removeOperational,
      setItems: workflow.setItems,
      attachExistingOperational,
      previewResult: workflow.previewResult,
      payable: workflow.payable ?? null,
      formError: workflow.formError,
      pendingReceipts: workflow.pendingReceipts,
      addPendingReceipts: workflow.addPendingReceipts,
      removePendingReceipt: workflow.removePendingReceipt,
      isPreviewing: workflow.isPreviewing,
      isCommitting: workflow.isCommitting,
      isLoadingPayable: workflow.isLoadingPayable,
      handlePublish: workflow.handlePublish,
      handleComplete: workflow.handleComplete,
      handleMarkPaid: workflow.handleMarkPaid,
      handleSave: workflow.handleSave,
      onEdit: canEdit ? onEdit : undefined
    }),
    [
      workflow.mode,
      workflow.readOnly,
      workflow.form,
      workflow.getForm,
      workflow.dirty,
      workflow.patchForm,
      workflow.operationalLinkLocked,
      workflow.removeOperational,
      workflow.setItems,
      workflow.previewResult,
      workflow.payable,
      workflow.formError,
      workflow.pendingReceipts,
      workflow.addPendingReceipts,
      workflow.removePendingReceipt,
      workflow.isPreviewing,
      workflow.isCommitting,
      workflow.isLoadingPayable,
      workflow.handlePublish,
      workflow.handleComplete,
      workflow.handleMarkPaid,
      workflow.handleSave,
      patchFormAndGoNext,
      goNext,
      attachExistingOperational,
      canEdit,
      onEdit
    ]
  )

  const stepDefs = useMemo(() => bindWizardSteps(PAYABLE_STEP_MODULES, stepCtx), [stepCtx])

  const initialStepId = useMemo(
    () =>
      initialStepForMode(
        mode,
        {
          ...workflow.form,
          orderId: orderIdSeed ?? workflow.form.orderId,
          payableId: payableId ?? workflow.form.payableId
        },
        workflow.hashStepSeed
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount seed
    []
  )

  const flow = useReportedWizardFlow(stepDefs, {
    initialStepId,
    stepSelection: readOnly ? 'locked' : 'gated',
    onNavStateChange
  })

  goNextAfterWriteRef.current = () => {
    void flow.goNext()
  }

  const seededFromPayableRef = useRef(false)
  useEffect(() => {
    if (payableId == null || payableId < 1) return
    if (workflow.isLoadingPayable || seededFromPayableRef.current) return
    if (workflow.form.payableId == null) return
    seededFromPayableRef.current = true
    flow.setActiveStepId(initialStepForMode(mode, workflow.form, workflow.hashStepSeed))
  }, [payableId, workflow.isLoadingPayable, workflow.form, mode, flow.setActiveStepId, workflow.hashStepSeed])

  const wasReadOnlyRef = useRef(readOnly)
  useEffect(() => {
    if (readOnly && !wasReadOnlyRef.current) flow.setActiveStepId(STEP_ID.REVIEW)
    wasReadOnlyRef.current = readOnly
  }, [readOnly, flow.setActiveStepId])

  const syncUrlRef = useRef(workflow.syncUrl)
  syncUrlRef.current = workflow.syncUrl

  useEffect(() => {
    const timer = setTimeout(() => syncUrlRef.current(flow.activeStepId), URL_SYNC_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [workflow.syncUrl, flow.activeStepId])

  useEffect(() => {
    if (flow.activeStepId !== STEP_ID.REVIEW) return
    if (!readOnly && !isPayableHostStepSatisfied(STEP_ID.PAYMENT, workflow.getForm())) {
      flow.setActiveStepId(STEP_ID.PAYMENT)
      return
    }
    void workflow.runPreviewForReview()
  }, [flow.activeStepId, flow.setActiveStepId, readOnly, workflow.getForm, workflow.runPreviewForReview])

  return <WizardFlowContent flow={flow} showNav={false} className="contents" />
}
